import { useMediaQuery } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useRef, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import { clientLogger, dispatchEvent, EventKey } from '@tgg/services';

import { useAppConfigContext, useTggTheme } from '../../contexts';
import withVerticalSpacing from '../../hocs/withVerticalSpacing';
import { ButtonBase } from '../Button';
import { IconButton } from '../IconButton';
import { ImageStandalone } from '../ImageStandalone';

import {
    StyledAutoplayButtonWrapper,
    StyledButtonBaseContainer,
    StyledCarouselBanner,
    StyledNavigationButton,
    StyledNavigationButtons,
    StyledProgressBar,
    StyledPromoPill,
    StyledSlideContent,
    StyledSubText,
    StyledTitle,
    StyledVideo,
} from './CarouselBanner.styled';
import { CarouselBannerProperties } from './CarouselBanner.types';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { isImage, getNextSlideIndex, getPreviousSlideIndex } from './utils';

const componentName = 'carousel';

const handleButtonBaseClick = (
    text: string,
    slideNumber: string,
    destination: string,
) =>
    dispatchEvent(EventKey.CTA_BUTTON, {
        cta_text: text,
        slide_number: slideNumber,
        component_name: componentName,
        cta_destination: destination,
    });

const handleNavigationButtonClick = () =>
    dispatchEvent(EventKey.CAROUSEL_INTERACTION, {
        event_type: 'carousel_click',
        component_name: componentName,
    });

/**
 * Use the `CarouselBanner` component to display a carousel of 3 images or videos.
 */
export const CarouselBannerComponent = ({
    id,
    slides,
}: CarouselBannerProperties) => {
    const {
        appConfig: { appIdentifier },
    } = useAppConfigContext();
    const { theme } = useTggTheme();
    const [progressPercent, setProgressPercent] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));
    const swiperReference = useRef<SwiperType>();
    const videoReference = useRef<HTMLVideoElement>(null);
    const onPlayingChange = () => {
        const video = videoReference.current;

        if (isPlaying) {
            setIsPlaying(false);
            setProgressPercent(100);
            video?.pause();
        } else {
            setIsPlaying(true);
            video?.play().catch(error => {
                clientLogger.error(
                    `Video play failed: ${error}`,
                    appIdentifier,
                );
            });
        }
    };

    return (
        <StyledCarouselBanner id={id}>
            <Swiper
                onBeforeInit={swiper => {
                    swiperReference.current = swiper;
                }}
                autoplay={{ delay: 6000, disableOnInteraction: false }}
                onSlideChange={swiper => setCurrentSlideIndex(swiper.realIndex)}
                loop={true}
                modules={[Autoplay, Pagination, Navigation]}
                onAutoplayTimeLeft={(_swiper, _timeLeft, percentage) =>
                    setProgressPercent(100 - percentage * 100)
                }
                className="mySwiper"
            >
                <PausePlayButton
                    isPlaying={isPlaying}
                    onPlayingChange={onPlayingChange}
                />
                {isDesktop && (
                    <StyledNavigationButtons>
                        <StyledNavigationButton>
                            <IconButton
                                aria-label={'Previous slide'}
                                buttonStyle="primary-inverted"
                                icon={'chevronLeft'}
                                onClick={() => {
                                    swiperReference.current?.slideToLoop(
                                        getPreviousSlideIndex(
                                            currentSlideIndex,
                                        ),
                                    );
                                    handleNavigationButtonClick();
                                }}
                            />
                        </StyledNavigationButton>
                        <StyledNavigationButton>
                            <IconButton
                                aria-label={'Next slide'}
                                buttonStyle="primary-inverted"
                                icon={'chevronRight'}
                                onClick={() => {
                                    swiperReference.current?.slideToLoop(
                                        getNextSlideIndex(currentSlideIndex),
                                    );
                                    handleNavigationButtonClick();
                                }}
                            />
                        </StyledNavigationButton>
                    </StyledNavigationButtons>
                )}
                {slides.map(
                    (
                        {
                            button,
                            title,
                            subText,
                            promoCode,
                            media,
                            secondaryTitle,
                        },
                        index,
                    ) => (
                        <SwiperSlide key={`${title}_${index}`}>
                            {isImage(media) ? (
                                <ImageStandalone
                                    isGif={media.isGif}
                                    image={{
                                        name: media.image.name,
                                        endpoint: media.image.endpoint,
                                        defaultHost: media.image.defaultHost,
                                    }}
                                    alt={media.alt || title}
                                    className="bannerImage"
                                />
                            ) : (
                                <StyledVideo
                                    ref={videoReference}
                                    src={`https://${media.video.defaultHost}/v/${media.video.endpoint}/${media.video.name}/mp4_720p`}
                                    autoPlay
                                    muted
                                    loop
                                    data-testid="video"
                                />
                            )}
                            <StyledSlideContent>
                                {promoCode && (
                                    <StyledPromoPill>
                                        <span className="swiper-no-swiping">
                                            Use code: {promoCode}
                                        </span>
                                    </StyledPromoPill>
                                )}
                                <StyledTitle>
                                    {title}
                                    {secondaryTitle && (
                                        <>
                                            <br /> {secondaryTitle}
                                        </>
                                    )}
                                </StyledTitle>
                                {subText && (
                                    <StyledSubText>{subText}</StyledSubText>
                                )}
                                <StyledButtonBaseContainer>
                                    <ButtonBase
                                        fullWidth
                                        text={button.text}
                                        href={button.href}
                                        onClick={() =>
                                            handleButtonBaseClick(
                                                button.text,
                                                `${index + 1}/${slides.length}`,
                                                button.href,
                                            )
                                        }
                                        buttonStyle="primary-alternate"
                                    />
                                </StyledButtonBaseContainer>
                            </StyledSlideContent>
                        </SwiperSlide>
                    ),
                )}
                <ProgressBar
                    progressPercent={progressPercent}
                    isPlaying={isPlaying}
                    currentSlideIndex={currentSlideIndex}
                    totalSlides={slides.length}
                />
            </Swiper>
        </StyledCarouselBanner>
    );
};

const PausePlayButton = ({
    isPlaying,
    onPlayingChange,
}: {
    isPlaying: boolean;
    onPlayingChange: () => void;
}) => {
    const { autoplay } = useSwiper();
    const buttonState = isPlaying ? 'pause' : 'play';
    const handleClick = () => {
        if (isPlaying) {
            autoplay.stop();
        } else {
            autoplay.start();
        }

        onPlayingChange();
        dispatchEvent(EventKey.CAROUSEL_INTERACTION, {
            event_type: `${buttonState}_carousel`,
            component_name: componentName,
        });
    };

    return (
        <StyledAutoplayButtonWrapper>
            <IconButton
                size="small"
                aria-label={buttonState}
                buttonStyle="primary-inverted"
                icon={buttonState}
                onClick={handleClick}
            />
        </StyledAutoplayButtonWrapper>
    );
};

const ProgressBar = ({
    progressPercent,
    isPlaying,
    currentSlideIndex,
    totalSlides,
}: {
    progressPercent: number;
    isPlaying: boolean;
    currentSlideIndex: number;
    totalSlides: number;
}) => {
    const swiper = useSwiper();

    const getBarValue = (index: number) => {
        if (currentSlideIndex === index) {
            return isPlaying ? progressPercent : 100;
        }
        return 0;
    };

    return (
        <StyledProgressBar>
            {Array.from({ length: totalSlides }, (_, index) => (
                <LinearProgress
                    key={index}
                    variant="determinate"
                    value={getBarValue(index)}
                    className="progressBar"
                    onClick={() => {
                        swiper.slideToLoop(index);
                        dispatchEvent(EventKey.CAROUSEL_INTERACTION, {
                            event_type: 'pips_click',
                            pip_position: `${index + 1}/${totalSlides}`,
                            component_name: componentName,
                        });
                    }}
                />
            ))}
        </StyledProgressBar>
    );
};

export const CarouselBanner = withVerticalSpacing(CarouselBannerComponent);
